import React, { useEffect, useState } from "react";
import { Doughnut, Bar } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from "chart.js";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import DatepickerDialog from "../../components/dialog/dateSelectDialog";
import {
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { Row } from "simple-flexbox";
// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const AdminDashboard = () => {
  const [walletData, setWalletData] = useState({
    winning_balance: 0,
    promotional_balance: 0,
    cash_deposite: 0,
    loan_distributed: 0,
    float_outstanding: 0,
  });
  const [joinDialog, setJoinDialog] = useState(false);
  const [counterSummary, setCounterSummary] = useState(null);

  const token = useSelector((state) => state.loginSlice.token);

  const handleDialogToggle = () => setJoinDialog(!joinDialog);

  const fetchWalletData = async () => {
    try {
      const res = await axiosRequest("GET", `${apiBaseUrl}/current-user-wallet`, token);
      if (res?.data) setWalletData(res.data);
    } catch (e) {
      console.error("Error fetching wallet data:", e);
    }
  };

  const fetchCounterSummary = async () => {
    try {
      const res = await axiosRequest("GET", `${apiBaseUrl}/shift-management/current-summary`, token);
      if (res?.data && Object.keys(res.data).length > 0) setCounterSummary(res.data);
      else setCounterSummary(null);
    } catch (e) {
      setCounterSummary(null);
    }
  };

  useEffect(() => {
    fetchWalletData();
    fetchCounterSummary();
    const interval = setInterval(() => {
      fetchWalletData();
      fetchCounterSummary();
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Container>
      <Row style={{ justifyContent: "space-between", width: "100%" }}>
        <Breadcrumb>
          <Link to="/">
            <h4>Dashboard</h4>
          </Link>
        </Breadcrumb>
      </Row>

      <Row>
        <DashboardContainer>
          <RevenueSection>
            {[
              { label: "Winning Balance",     value: walletData.winning_balance },
              { label: "Promo Balance",        value: walletData.promotional_balance },
              { label: "Cash Deposited",       value: walletData.cash_deposite },
              { label: "Loan Distributed",     value: walletData.loan_distributed },
              { label: "Float Outstanding",    value: walletData.float_outstanding },
            ].map(({ label, value }, index) => (
              <StatBox key={index}>
                <RowDiv>
                  <p>{label}</p>
                </RowDiv>
                <RowDiv>
                  <h3>{Number(value || 0).toLocaleString("en-IN")}</h3>
                </RowDiv>
              </StatBox>
            ))}
          </RevenueSection>
        </DashboardContainer>
      </Row>

      {/* Counter Cash & Chips */}
      {counterSummary && (
        <Box mt={3}>
          <Grid container spacing={3}>

            {/* Offer Distributed This Shift */}
            {(counterSummary.offer_distributed?.total > 0 || counterSummary.offer_distributed?.count > 0) && (
              <Grid item xs={12}>
                <Paper sx={{
                  borderRadius: 3, overflow: "hidden",
                  boxShadow: "0 4px 16px rgba(173,20,87,0.15)",
                  border: "2px solid #e91e8c",
                }}>
                  <Box sx={{
                    background: "linear-gradient(135deg, #880e4f 0%, #e91e8c 100%)",
                    px: 3, py: 1.5,
                    display: "flex", alignItems: "center", gap: 2,
                  }}>
                    <Typography variant="h6" fontWeight={800} color="#fff">🎁 Offer Distributed This Shift</Typography>
                    <Chip
                      label={`${(counterSummary.offer_distributed?.total || 0).toLocaleString("en-IN")}`}
                      sx={{ background: "#1c2433", color: "#880e4f", fontWeight: 800, fontSize: "1rem", px: 1, ml: "auto" }}
                    />
                    <Chip
                      label={`${counterSummary.offer_distributed?.count || 0} txns`}
                      sx={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700 }}
                    />
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Remaining Cash */}
            <Grid item xs={12} md={6}>
              <Paper sx={{
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 8px 24px rgba(56,142,60,0.18)",
                border: "2px solid #43a047",
              }}>
                <Box sx={{
                  background: "linear-gradient(135deg, #1b5e20 0%, #43a047 100%)",
                  px: 3, py: 2,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <Typography variant="h6" fontWeight={800} color="#fff" letterSpacing={1}>
                    💵 Remaining Cash
                  </Typography>
                  <Chip
                    label={`${(counterSummary.counter_cash?.total || 0).toLocaleString("en-IN")}`}
                    sx={{ background: "#1c2433", color: "#81c784", fontWeight: 800, fontSize: "1rem", px: 1 }}
                  />
                </Box>
                <Box sx={{ p: 2, background: "#f1f8e9" }}>
                  <Grid container spacing={1}>
                    {Object.entries(counterSummary.counter_cash?.denominations || {})
                      .sort((a, b) => Number(a[0]) - Number(b[0]))
                      .map(([d, q]) => (
                        <Grid item key={d}>
                          <Paper elevation={3} sx={{
                            px: 2, py: 1.5, borderRadius: 2, textAlign: "center", minWidth: 90,
                            background: "linear-gradient(135deg, #a5d6a7, #c8e6c9)",
                            border: "1px solid #81c784",
                          }}>
                            <Typography variant="caption" fontWeight={700} color="#1b5e20">{Number(d).toLocaleString()}</Typography>
                            <Typography variant="h6" fontWeight={800} color="#2e7d32">{q}</Typography>
                            <Typography variant="caption" color="#388e3c" fontWeight={600}>pcs</Typography>
                            <Divider sx={{ my: 0.5 }} />
                            <Typography variant="caption" fontWeight={700} color="#1b5e20">
                              {(Number(d) * Number(q)).toLocaleString("en-IN")}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    {Object.keys(counterSummary.counter_cash?.denominations || {}).length === 0 && (
                      <Grid item xs={12}><Typography variant="body2" color="text.secondary">No cash on counter</Typography></Grid>
                    )}
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            {/* Remaining Chips */}
            <Grid item xs={12} md={6}>
              <Paper sx={{
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 8px 24px rgba(21,101,192,0.18)",
                border: "2px solid #1e88e5",
              }}>
                <Box sx={{
                  background: "linear-gradient(135deg, #0d47a1 0%, #1e88e5 100%)",
                  px: 3, py: 2,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <Typography variant="h6" fontWeight={800} color="#fff" letterSpacing={1}>
                    🎰 Remaining Chips
                  </Typography>
                  <Chip
                    label={`${(counterSummary.counter_chips?.total || 0).toLocaleString("en-IN")}`}
                    sx={{ background: "#1c2433", color: "#0d47a1", fontWeight: 800, fontSize: "1rem", px: 1 }}
                  />
                </Box>
                <Box sx={{ p: 2, background: "#0f2740" }}>
                  <Grid container spacing={1}>
                    {Object.entries(counterSummary.counter_chips?.denominations || {})
                      .sort((a, b) => Number(a[0]) - Number(b[0]))
                      .map(([d, q]) => (
                        <Grid item key={d}>
                          <Paper elevation={3} sx={{
                            px: 2, py: 1.5, borderRadius: 2, textAlign: "center", minWidth: 90,
                            background: "linear-gradient(135deg, #90caf9, #bbdefb)",
                            border: "1px solid #64b5f6",
                          }}>
                            <Typography variant="caption" fontWeight={700} color="#0d47a1">{Number(d).toLocaleString()}</Typography>
                            <Typography variant="h6" fontWeight={800} color="#1565c0">{q}</Typography>
                            <Typography variant="caption" color="#1976d2" fontWeight={600}>pcs</Typography>
                            <Divider sx={{ my: 0.5 }} />
                            <Typography variant="caption" fontWeight={700} color="#0d47a1">
                              {(Number(d) * Number(q)).toLocaleString("en-IN")}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    {Object.keys(counterSummary.counter_chips?.denominations || {}).length === 0 && (
                      <Grid item xs={12}><Typography variant="body2" color="text.secondary">No chips on counter</Typography></Grid>
                    )}
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      <DatepickerDialog open={joinDialog} handleClose={handleDialogToggle} join = {true} label={"Join Contest"}/>
    </Container>
  );
};

export default AdminDashboard;

// Styled Components
const Container = styled.div`
  margin-top: 95px;
  padding: 16px;
  background-color: #f8f8f8;
  padding-bottom: 50px;

  @media (max-width: 768px) {
    margin-top: 55px;
    padding: 12px;
  }
`;

const Breadcrumb = styled.div`
  margin-bottom: 20px;
  h4 {
    font-size: 1.2rem;
    color: #007bff;
  }
  a {
    text-decoration: none;
  }
`;

const DashboardContainer = styled.div`
  background-color: #ffffff;
  padding: 20px;
  border-radius: 8px;
  width: 100%;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
`;

const RevenueSection = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 15px;
  text-align: center;
  flex-wrap: wrap;
`;

export const StatBox = styled.div`
  flex: 1 1 160px;
  min-width: 140px;
  background-color: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
  }

  h3 {
    margin: 10px 0;
    font-size: 1.5rem;
    color: #000000;
  }

  p {
    color: #666666;
    font-size: 0.9rem;
  }
`;

export const RowDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;
